import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}
export function Drawer({
  isOpen,
  onClose,
  children,
  title,
  subtitle
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Escape to close + focus trap
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    const focusable = panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    focusable?.[0]?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && focusable && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex justify-end" data-id="element-551" data-mp="3gmy58235dyca">
      <div className="absolute inset-0 bg-ink/45 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} aria-hidden="true" data-id="element-552" data-mp="3lnluai7tg53v" />

      <div ref={panelRef} className="relative w-full max-w-md bg-background h-full shadow-warm flex flex-col animate-in slide-in-from-right duration-300 ease-out rounded-l-[1.75rem] overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="drawer-title" data-id="element-553" data-mp="2t07zapnohjme">
        <div className="flex items-start justify-between gap-4 p-6 bg-surface border-b border-border" data-id="element-554" data-mp="2kxz2r0ktr0u0">
          <div data-id="element-555" data-mp="2zmv98t78g5si">
            <h2 id="drawer-title" className="text-2xl font-serif text-ink leading-tight" data-id="element-556" data-mp="2znddod41tqbi">
              {title}
            </h2>
            {subtitle && <p className="mt-1 text-sm text-muted-ink" data-id="element-557" data-mp="26zr9p3qwsjo4">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 -mr-1 rounded-full text-muted-ink hover:text-ink hover:bg-soft-panel transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label="Close" data-id="element-558" data-mp="221pfpnry2zxv">
            <X className="w-5 h-5" data-id="element-559" data-mp="3sbizz4ar3ndg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-560" data-mp="3ndr644qjpr6m">{children}</div>
      </div>
    </div>;
}